import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get, noop } from 'lodash-es';
import debounce from 'lodash-es/debounce';

import './sticky-to-bottom.scss';

const DEBOUNCE_TIME = 100;

function getOffsetFromWrapper(el, wrapper) {
  let top = 0;
  let current = el;
  while (current && current !== wrapper) {
    top += current.offsetTop;
    current = current.offsetParent;
  }
  return top;
}

export function StickyToBottom({
  children,
  contentWrapperClassName = null,
  enabled = true,
  offsetSelector = null,
  additionalOffset = 0,
  topAnchorSelector = null,
}) {
  const [viewportHeight, setViewportHeight] = useState(0);
  const [elementHeight, setElementHeight] = useState(0);
  const [offsetHeight, setOffsetHeight] = useState(0);
  const [anchorOffset, setAnchorOffset] = useState(0);
  const stickyContentRef = useRef();

  const updateAnchorOffset = () => {
    if (topAnchorSelector) {
      setAnchorOffset(
        getOffsetFromWrapper(stickyContentRef.current?.querySelector(topAnchorSelector), stickyContentRef.current)
      );
    }
  };

  // eslint-disable-next-line react-hooks/refs -- debounced DOM measurement runs after render.
  const getElementHeight = debounce(() => {
    setElementHeight(get(stickyContentRef, 'current.offsetHeight', 0));
    updateAnchorOffset();
  }, DEBOUNCE_TIME);

  const getViewportHeight = debounce(() => {
    setViewportHeight(window.innerHeight);
  }, DEBOUNCE_TIME);

  useEffect(() => {
    if (!enabled) {
      return noop;
    }

    // Intentional initial DOM measurement for sticky positioning.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setViewportHeight(window.innerHeight);
    setElementHeight(get(stickyContentRef, 'current.offsetHeight', 0));
    setOffsetHeight(get(document.querySelector(offsetSelector), 'offsetHeight', 0) + additionalOffset);
    updateAnchorOffset();

    window.addEventListener('resize', getViewportHeight);

    return () => {
      window.removeEventListener('resize', getViewportHeight);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!enabled) {
    return children({});
  }

  const isInViewport = viewportHeight - offsetHeight - elementHeight >= 0;
  const isAnchorMode = !isInViewport && !!anchorOffset;

  return (
    <div
      className={classnames('sticky-to-bottom h-100 d-flex flex-column', {
        'in-viewport': isInViewport || isAnchorMode,
      })}
    >
      <div
        ref={stickyContentRef}
        className={classnames('sticky-content-wrapper', contentWrapperClassName)}
        style={isInViewport || isAnchorMode ? { top: offsetHeight - anchorOffset } : undefined}
      >
        {children({ onHeightUpdate: getElementHeight })}
      </div>
    </div>
  );
}

StickyToBottom.propTypes = {
  children: PropTypes.func.isRequired,
  offsetSelector: PropTypes.string,
  additionalOffset: PropTypes.number,
  contentWrapperClassName: PropTypes.string,
  enabled: PropTypes.bool,
  topAnchorSelector: PropTypes.string,
};
